import apiClient from './api.ts';
import { selectedAcademicYearRequestId } from '@/lib/academic-year-context';
import {
    CourseSchema,
    CourseSectionSchema,
    SubjectSchema,
    TeacherSpecializationSchema,
    CreateTeacherSpecializationSchema,
    UpdateTeacherSpecializationSchema,
    TeacherAssignmentSchema,
    StudentEnrollmentSchema,
    CourseAnalyticsSchema,
    SectionStatsSchema,
    WorkloadConstraintSchema,
    LegacyCourseSchema,
    CreateCourseWithSubjectSchema,
    AssignCourseClassSchema,
    ClassCourseConfigSchema,
    UpdateCourseSchema,
    MultiBranchCourseQueryParams
} from "@/types/course_schema.ts";

export default {
    // ==================== Course Management ====================

    // Legacy methods for backward compatibility
    async getAllCourses(isActive: boolean, subjectId?: number, academicYearId?: number, gradeId?: number, page: number = 1, limit: number = 20, sort: string = 'id', order: 'asc' | 'desc' = 'asc', classId?: number) {
        const params = new URLSearchParams({
            is_active: isActive.toString(),
            page: page.toString(),
            limit: limit.toString(),
            sort,
            order
        });
        if (subjectId !== undefined) params.append('subject_id', subjectId.toString());
        if (academicYearId !== undefined) params.append('academic_year_id', academicYearId.toString());
        if (gradeId !== undefined) params.append('grade_id', gradeId.toString());
        if (classId !== undefined) params.append('class_id', classId.toString());
        return await apiClient.get(`courses?${params}`);
    },

    async getAllCoursesForClass(classId: string, page: number = 1, limit: number = 20, sort: string = 'id', order: 'asc' | 'desc' = 'asc') {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
            sort,
            order
        });
        return await apiClient.get(`courses/class/${classId}?${params}`);
    },

    async getAllCoursesForGrades(schoolId: number, gradeIds: number[], page: number = 1, limit: number = 20, sort: string = 'id', order: 'asc' | 'desc' = 'asc') {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
            sort,
            order
        });
        return await apiClient.post(`courses/school/${schoolId}/grades?${params}`, { grade_ids: gradeIds });
    },

    async getAllCoursesForGradesAndClasses(schoolId: number, gradeIds: number[], classIds: number[], page: number = 1, limit: number = 20, sort: string = 'id', order: 'asc' | 'desc' = 'asc') {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
            sort,
            order
        });
        return await apiClient.post(`courses/school/${schoolId}/grades?${params}`, { grade_ids: gradeIds, class_ids: classIds });
    },

    // New API methods matching backend documentation
    async getCoursesBySubject(subjectId: number, academicYearId: number, branchId: string, page: number = 1, pageSize: number = 20) {
        const params = new URLSearchParams({
            academic_year_id: academicYearId.toString(),
            branch_id: branchId,
            page: page.toString(),
            page_size: pageSize.toString()
        });
        return await apiClient.get(`courses/subject/${subjectId}?${params}`);
    },

    async getCourseDetails(courseId: number) {
        return await apiClient.get(`courses/${courseId}`);
    },

    // New subject-based course creation
    async createCourseWithSubject(data: CreateCourseWithSubjectSchema) {
        return await apiClient.post('courses/with-subject', data);
    },

    async createCourse(course: Partial<CourseSchema>) {
        return await apiClient.post('courses', course);
    },

    async updateCourse(courseId: number, schoolId: string | number, course: UpdateCourseSchema) {
        return await apiClient.put(`courses/${courseId}/school/${schoolId}`, course);
    },

    async deleteCourse(courseId: number) {
        return await apiClient.delete(`courses/${courseId}`);
    },

    async archiveCourse(courseId: number) {
        return await apiClient.put(`courses/${courseId}/archive`);
    },

    async unarchiveCourse(courseId: number) {
        return await apiClient.put(`courses/${courseId}/unarchive`);
    },

    // New method for assigning course to class
    async assignCourseToClass(data: AssignCourseClassSchema) {
        return await apiClient.post('courses/assign-course-class', data);
    },

    // Set per-class+course scheduling config (course must already be assigned to the class).
    // Send weekly_hours/session_length_minutes as null to clear the override.
    async setClassCourseConfig(data: ClassCourseConfigSchema) {
        return await apiClient.put('courses/class-course-config', data);
    },

    // Legacy method for compatibility
    async assignCourseToClassLegacy(classId: number, courseId: number) {
        return await apiClient.post(`courses/assign-course-class`, {
            class_id: classId,
            course_id: courseId,
        });
    },

    async unassignCourseFromClass(courseId: number, classId: number) {
        const academicYearId = selectedAcademicYearRequestId();
        const params = academicYearId ? `?academic_year_id=${encodeURIComponent(academicYearId)}` : '';
        return await apiClient.post(`courses/unassign-course-class${params}`, {
            class_id: classId,
            course_id: courseId,
        });
    },

    // ==================== Course Section Management ====================

    async createCourseSection(courseId: number, section: Partial<CourseSectionSchema>) {
        return await apiClient.post(`courses/${courseId}/sections`, section);
    },

    async getCourseSections(courseId: number, academicYearId: number) {
        const params = new URLSearchParams({
            academic_year_id: academicYearId.toString()
        });
        return await apiClient.get(`courses/${courseId}/sections?${params}`);
    },

    async updateCourseSection(sectionId: number, section: Partial<CourseSectionSchema>) {
        return await apiClient.put(`courses/sections/${sectionId}`, section);
    },

    async deleteCourseSection(sectionId: number) {
        return await apiClient.delete(`courses/sections/${sectionId}`);
    },

    // ==================== Student Enrollment Management ====================

    async enrollStudentInSection(sectionId: number, studentId: number) {
        return await apiClient.post(`courses/sections/${sectionId}/enroll`, {
            student_id: studentId
        });
    },

    async removeStudentFromSection(sectionId: number, studentId: number) {
        return await apiClient.delete(`courses/sections/${sectionId}/students/${studentId}`);
    },

    async getSectionStudents(sectionId: number) {
        return await apiClient.get(`courses/sections/${sectionId}/students`);
    },

    async getStudentSections(studentId: number, academicYearId: number) {
        const params = new URLSearchParams({
            academic_year_id: academicYearId.toString()
        });
        return await apiClient.get(`students/${studentId}/sections?${params}`);
    },

    // ==================== Teacher Assignment Management ====================

    async assignTeacherToSection(sectionId: number, teacherId: number) {
        return await apiClient.post(`courses/sections/${sectionId}/teachers`, {
            teacher_id: teacherId
        });
    },

    async getSectionTeachers(sectionId: number) {
        return await apiClient.get(`courses/sections/${sectionId}/teachers`);
    },

    async getTeacherSections(teacherId: number, academicYearId: number) {
        const params = new URLSearchParams({
            academic_year_id: academicYearId.toString()
        });
        return await apiClient.get(`teachers/${teacherId}/sections?${params}`);
    },

    // ==================== Teacher Specialization Management ====================

    /**
     * Get teacher's specializations
     * GET /teachers/:teacherId/specializations
     */
    async getTeacherSpecializations(teacherId: number) {
        return await apiClient.get(`teachers/${teacherId}/specializations`);
    },

    /**
     * Get all specializations for a subject
     * GET /subjects/:subjectId/specializations
     * @param subjectId - Subject ID
     * @param willingOnly - Filter only teachers willing to teach (default: false)
     */
    async getSubjectSpecializations(subjectId: number, willingOnly: boolean = false) {
        const params = new URLSearchParams();
        if (willingOnly) params.append('willing_only', 'true');
        return await apiClient.get(`subjects/${subjectId}/specializations?${params}`);
    },

    /**
     * Create teacher specialization for a subject
     * POST /subjects/:subjectId/specializations
     */
    async createSubjectSpecialization(subjectId: number, data: Omit<CreateTeacherSpecializationSchema, 'subject_id'>) {
        return await apiClient.post(`subjects/${subjectId}/specializations`, data);
    },

    /**
     * Update teacher specialization
     * PUT /subjects/:subjectId/specializations/:specializationId
     */
    async updateSubjectSpecialization(subjectId: number, specializationId: number, data: UpdateTeacherSpecializationSchema) {
        return await apiClient.put(`subjects/${subjectId}/specializations/${specializationId}`, data);
    },

    /**
     * Delete teacher specialization
     * DELETE /subjects/:subjectId/specializations/:specializationId
     */
    async deleteSubjectSpecialization(subjectId: number, specializationId: number) {
        return await apiClient.delete(`subjects/${subjectId}/specializations/${specializationId}`);
    },

    // ==================== Teacher Workload Management ====================

    async createWorkloadConstraint(teacherId: number, constraint: Partial<WorkloadConstraintSchema>) {
        return await apiClient.post(`teachers/${teacherId}/workload-constraints`, constraint);
    },

    async getTeacherWorkload(teacherId: number, academicYearId: number) {
        const params = new URLSearchParams({
            academic_year_id: academicYearId.toString()
        });
        return await apiClient.get(`teachers/${teacherId}/workload?${params}`);
    },

    async getTeachersAtCapacity(academicYearId: number, threshold: number = 90.0) {
        const params = new URLSearchParams({
            academic_year_id: academicYearId.toString(),
            threshold: threshold.toString()
        });
        return await apiClient.get('workload/teachers/at-capacity?${params}');
    },

    async getAvailableTeachers(academicYearId: number, subjectId: number) {
        const params = new URLSearchParams({
            academic_year_id: academicYearId.toString(),
            subject_id: subjectId.toString()
        });
        return await apiClient.get('workload/teachers/available?${params}');
    },

    // ==================== Subject Management ====================

    async getAllSubjects(isActive: boolean = true, category: string = '', page: number = 1, pageSize: number = 50) {
        const params = new URLSearchParams({
            is_active: isActive.toString(),
            page: page.toString(),
            page_size: pageSize.toString()
        });
        if (category) params.append('category', category);

        return await apiClient.get(`subjects?${params}`);
    },

    async getSubjectCategories() {
        return await apiClient.get('subjects/categories');
    },

    // ==================== Analytics and Reporting ====================

    async getSectionStatistics(sectionId: number) {
        return await apiClient.get(`courses/sections/${sectionId}/stats`);
    },

    async getCourseAnalytics(courseId: number, academicYearId: number) {
        const params = new URLSearchParams({
            academic_year_id: academicYearId.toString()
        });
        return await apiClient.get(`courses/${courseId}/analytics?${params}`);
    },

    // ==================== Multi-Branch Course Queries ====================

    /**
     * Get courses across multiple branches with advanced filtering
     * @param queryParams - Query parameters including branch_ids and optional filters
     * @returns Course offerings organized by branch and academic year
     */
    async getCoursesByBranch(queryParams: MultiBranchCourseQueryParams) {
        const params = new URLSearchParams();

        // Handle branch_ids - can be string or array
        if (Array.isArray(queryParams.branch_ids)) {
            queryParams.branch_ids.forEach(id => params.append('branch_ids', id));
        } else {
            params.append('branch_ids', queryParams.branch_ids);
        }

        // Add optional filters
        if (queryParams.academic_year_id !== undefined) {
            params.append('academic_year_id', queryParams.academic_year_id.toString());
        }
        if (queryParams.subject_id !== undefined) {
            params.append('subject_id', queryParams.subject_id.toString());
        }
        if (queryParams.grade_id !== undefined) {
            params.append('grade_id', queryParams.grade_id.toString());
        }
        if (queryParams.class_id !== undefined) {
            params.append('class_id', queryParams.class_id.toString());
        }
        if (queryParams.is_active !== undefined) {
            params.append('is_active', queryParams.is_active.toString());
        }
        if (queryParams.status) {
            params.append('status', queryParams.status);
        }
        if (queryParams.course_name) {
            params.append('course_name', queryParams.course_name);
        }
        if (queryParams.page !== undefined) {
            params.append('page', queryParams.page.toString());
        }
        if (queryParams.page_size !== undefined) {
            params.append('page_size', queryParams.page_size.toString());
        }

        return await apiClient.get(`courses/by-branch?${params.toString()}`);
    },

    // ==================== Teacher Role Management ====================

    /**
     * Get available teacher roles for course assignments
     * GET /courses/teacher-roles
     */
    async getTeacherRoles() {
        return await apiClient.get('courses/teacher-roles');
    },

    /**
     * Get available homeroom roles
     * GET /courses/homeroom-roles
     */
    async getHomeroomRoles() {
        return await apiClient.get('courses/homeroom-roles');
    },

    /**
     * Get classes linked to a course
     * GET /courses/:courseId/classes
     */
    async getClassesForCourse(courseId: number, page: number = 1, limit: number = 50) {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        });
        return await apiClient.get(`courses/${courseId}/classes?${params}`);
    },
};
